import type { WidgetViewState } from "../types.js";
import type { Strings } from "../state.js";
import { deriveDisplayState } from "../state.js";
import { Trigger } from "./Trigger.js";
import { Panel } from "./Panel.js";
import { ChevronDownIcon } from "./icons.js";
import type { TranscriptMessage } from "../types.js";
import type { ResolvedOrbPalette } from "../orbPalette.js";

export interface ShellIntents {
  onTriggerClick(): void;
  onStart(): void;
  onStop(): void;
  onCollapse(): void;
  onToggleMute(): void;
  onSendText(text: string): void | Promise<void>;
}

export interface ShellProps {
  state: WidgetViewState;
  strings: Strings;
  muteButton?: boolean;
  showTranscript?: boolean;
  textInput?: boolean;
  transcript?: readonly TranscriptMessage[];
  palette?: ResolvedOrbPalette;
  intents: ShellIntents;
}

export function Shell({
  state,
  strings,
  muteButton,
  showTranscript,
  textInput,
  transcript = [],
  palette,
  intents,
}: ShellProps) {
  const displayState = deriveDisplayState(state.status, state.mode);
  const active = state.status === "connecting" || state.status === "connected";
  const showPanel = state.variant === "bar" || state.expanded;
  const collapsible = state.variant !== "bar";

  return (
    <div class="va-shell">
      {showPanel ? (
        <>
          <Panel
            displayState={displayState}
            strings={strings}
            active={active}
            muted={state.muted}
            muteButton={muteButton}
            showTranscript={showTranscript}
            textInput={textInput}
            transcript={transcript}
            palette={palette}
            onStart={intents.onStart}
            onStop={intents.onStop}
            onToggleMute={intents.onToggleMute}
            onSendText={intents.onSendText}
          />
          {collapsible ? (
            <button
              type="button"
              class="va-collapse-float"
              aria-label={strings.minimize}
              onClick={intents.onCollapse}
            >
              <ChevronDownIcon />
            </button>
          ) : null}
        </>
      ) : (
        <Trigger
          label={strings.idle}
          strings={strings}
          displayState={displayState}
          active={active}
          onClick={intents.onTriggerClick}
          onStop={intents.onStop}
          onExpand={intents.onTriggerClick}
          palette={palette}
        />
      )}
    </div>
  );
}
